import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Heart, Moon, Snowflake, Activity, Droplets, Hand, Footprints, Circle,
  AlertTriangle, CheckCircle2, Clock, ChevronRight, Plus, X, Play, Move
} from "lucide-react";
import { toast } from "sonner";
import RecoveryCoachCard from "@/components/RecoveryCoachCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_ICONS = {
  foam_rolling: Circle,
  ice_bath: Snowflake,
  stretching: Move,
  sleep: Moon,
  massage: Hand,
  contrast_shower: Droplets,
  yoga: Heart,
  walk: Footprints,
};

const PRIORITY_STYLES = {
  HIGH: { color: "#FF4500", label: "PRIORITY" },
  MEDIUM: { color: "#CCFF00", label: "RECOMMEND" },
  LOW: { color: "#3b82f6", label: "OPTIONAL" },
};

const Recovery = () => {
  const [score, setScore] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [history, setHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [types, setTypes] = useState({});
  const [showLog, setShowLog] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [logForm, setLogForm] = useState({
    type: "stretching",
    duration_minutes: 10,
    sleep_hours: 8,
    sleep_quality: 4,
    soreness_level: 3,
    soreness_areas: [],
    notes: "",
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [s, r, h, sess, t] = await Promise.all([
        axios.get(`${API}/recovery/score`, { headers }),
        axios.get(`${API}/recovery/recommendations`, { headers }),
        axios.get(`${API}/recovery/history?days=7`, { headers }),
        axios.get(`${API}/recovery/sessions`, { headers }),
        axios.get(`${API}/recovery/types`, { headers }),
      ]);
      setScore(s.data);
      setRecommendations(r.data.recommendations || []);
      setHistory(h.data.logs || []);
      setSessions(sess.data.sessions || []);
      setTypes(t.data.types || {});
    } catch (err) {
      console.error("Recovery load failed", err);
    }
  };

  const submitLog = async () => {
    try {
      const payload = { ...logForm };
      // Trim payload based on type
      if (payload.type !== "sleep") {
        delete payload.sleep_hours;
        delete payload.sleep_quality;
      } else {
        delete payload.duration_minutes;
      }
      await axios.post(`${API}/recovery/log`, payload, { headers });
      toast.success("Recovery logged", {
        action: { label: "Undo", onClick: () => resetLastRecovery() },
        duration: 8000,
      });
      setShowLog(false);
      fetchAll();
    } catch (err) {
      toast.error("Failed to log recovery");
    }
  };

  const resetLastRecovery = async () => {
    try {
      await axios.delete(`${API}/recovery/last`, { headers });
      toast.success("Last recovery entry reset");
      fetchAll();
    } catch {
      toast.error("Nothing to reset");
    }
  };

  const resetRecoveryEntry = async (logId) => {
    try {
      await axios.delete(`${API}/recovery/log/${logId}`, { headers });
      toast.success("Entry deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleArea = (area) => {
    setLogForm((f) => ({
      ...f,
      soreness_areas: f.soreness_areas.includes(area)
        ? f.soreness_areas.filter((a) => a !== area)
        : [...f.soreness_areas, area],
    }));
  };

  const SORENESS_AREAS = ["quads", "hamstrings", "calves", "glutes", "back", "shoulders", "knees", "ankles"];

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative" data-testid="recovery-page">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto space-y-8"
      >
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">
              Pivot Point
            </p>
            <h1 className="text-5xl md:text-7xl font-heading text-white uppercase tracking-tight leading-none">
              Recovery
            </h1>
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mt-2">
              Rest. Repair. Reload.
            </p>
          </div>
          <Button
            onClick={() => setShowLog(true)}
            className="bg-[#FF4500] hover:bg-[#FF6326] font-heading uppercase tracking-wider text-xs rounded-none"
            data-testid="log-recovery-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Log Recovery
          </Button>
        </div>

        {/* AI Recovery Coach */}
        <RecoveryCoachCard />

        {/* Recovery Score */}
        {score && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-8" data-testid="recovery-score-card">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              {/* Score Dial */}
              <div className="flex flex-col items-center">
                <div className="relative w-44 h-44">
                  <svg className="w-full h-full -rotate-90">
                    <circle cx="50%" cy="50%" r="78" stroke="rgba(255,255,255,0.06)" strokeWidth="6" fill="none" />
                    <circle
                      cx="50%"
                      cy="50%"
                      r="78"
                      stroke={score.color}
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${(score.score / 100) * 490} 490`}
                      strokeLinecap="square"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-6xl font-heading text-white" data-testid="recovery-score-value">
                      {score.score}
                    </span>
                    <span className="text-[9px] font-mono text-white/30 uppercase tracking-[0.3em] mt-1">
                      Readiness
                    </span>
                  </div>
                </div>
                <div className="mt-4 px-4 py-1.5 border" style={{ borderColor: score.color, color: score.color }}>
                  <span className="font-heading text-sm uppercase tracking-[0.2em]">{score.status}</span>
                </div>
              </div>

              {/* Status Message + Factors */}
              <div className="md:col-span-2 space-y-4">
                <div>
                  <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-2">
                    System Status
                  </p>
                  <p className="text-xl text-white font-medium leading-snug">{score.message}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06]">
                  {Object.entries(score.factors).map(([k, v]) => (
                    <div key={k} className="bg-[#0A0A0A] p-3">
                      <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mb-1">
                        {k.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm font-heading text-white uppercase">{String(v)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div>
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-3">
              AI Recommendations
            </p>
            <div className="space-y-2" data-testid="recovery-recommendations">
              {recommendations.map((rec, i) => {
                const style = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.LOW;
                const Icon = rec.priority === "HIGH" ? AlertTriangle : CheckCircle2;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-[#0A0A0A] border-l-2 border-white/[0.06] p-4 flex items-start gap-4 hover:border-l-4 transition-all"
                    style={{ borderLeftColor: style.color }}
                  >
                    <Icon className="w-5 h-5 mt-1 shrink-0" style={{ color: style.color }} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-0.5"
                          style={{ color: style.color, borderColor: style.color, border: "1px solid" }}
                        >
                          {style.label}
                        </span>
                        <p className="text-sm font-heading text-white uppercase tracking-tight">
                          {rec.title}
                        </p>
                      </div>
                      <p className="text-xs text-white/50 leading-relaxed">{rec.message}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Recovery Sessions */}
        <div>
          <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-3">
            Active Recovery Sessions
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="recovery-sessions">
            {sessions.map((s) => (
              <motion.div
                key={s.id}
                whileHover={{ y: -2 }}
                onClick={() => setActiveSession(s)}
                className="bg-[#0A0A0A] border border-white/[0.06] hover:border-[#FF4500] cursor-pointer p-5 transition-all"
                data-testid={`session-${s.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Activity className="w-6 h-6 text-[#FF4500]" />
                  <div className="flex items-center gap-1 text-white/40">
                    <Clock className="w-3 h-3" />
                    <span className="text-[10px] font-mono">{s.duration_minutes}M</span>
                  </div>
                </div>
                <p className="text-base font-heading text-white uppercase tracking-tight mb-1">{s.name}</p>
                <p className="text-[11px] text-white/40 mb-3 leading-snug">{s.description}</p>
                <div className="flex items-center justify-between">
                  <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em]">
                    {s.exercises.length} Exercises
                  </p>
                  <ChevronRight className="w-4 h-4 text-[#FF4500]" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Recovery Activity Types */}
        <div>
          <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-3">
            Recovery Toolkit
          </p>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-px bg-white/[0.06]">
            {Object.entries(types).map(([key, t]) => {
              const Icon = TYPE_ICONS[key] || Activity;
              return (
                <button
                  key={key}
                  onClick={() => {
                    setLogForm((f) => ({ ...f, type: key }));
                    setShowLog(true);
                  }}
                  className="bg-[#0A0A0A] hover:bg-white/[0.03] p-4 flex flex-col items-center gap-2 transition-all"
                  data-testid={`tool-${key}`}
                >
                  <Icon className="w-5 h-5" style={{ color: t.color }} />
                  <span className="text-[9px] font-mono text-white/50 uppercase tracking-[0.1em] text-center">
                    {t.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent History */}
        {history.length > 0 && (
          <div>
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-3">
              Last 7 Days
            </p>
            <div className="space-y-1" data-testid="recovery-history">
              {history.slice(0, 10).map((log, i) => {
                const Icon = TYPE_ICONS[log.type] || Activity;
                const date = new Date(log.date);
                return (
                  <div
                    key={log.id || i}
                    className="bg-[#0A0A0A] border border-white/[0.06] p-3 flex items-center gap-3 group"
                  >
                    <Icon className="w-4 h-4 text-[#FF4500] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white capitalize">{log.type.replace(/_/g, " ")}</p>
                      {log.notes && <p className="text-[10px] text-white/40 truncate">{log.notes}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      {log.duration_minutes && (
                        <p className="text-xs font-mono text-white/50">{log.duration_minutes}m</p>
                      )}
                      {log.sleep_hours && (
                        <p className="text-xs font-mono text-white/50">{log.sleep_hours}h sleep</p>
                      )}
                      <p className="text-[9px] font-mono text-white/30">
                        {date.toLocaleDateString([], { month: "short", day: "numeric" })}
                      </p>
                    </div>
                    {log.id && (
                      <button
                        onClick={() => resetRecoveryEntry(log.id)}
                        className="text-white/20 hover:text-[#FF4500] transition-colors p-1"
                        title="Reset / delete this entry"
                        data-testid={`reset-recovery-${log.id}`}
                        aria-label="Reset entry"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>

      {/* Log Modal */}
      {showLog && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowLog(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] border border-white/[0.06] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            data-testid="log-recovery-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Log Recovery Entry"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] font-mono text-[#FF4500] uppercase tracking-[0.3em]">Log Activity</p>
                <h3 className="text-2xl font-heading text-white uppercase">Recovery Entry</h3>
              </div>
              <button onClick={() => setShowLog(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Type Selector */}
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-2">Activity Type</p>
            <div className="grid grid-cols-4 gap-px bg-white/[0.06] mb-5">
              {Object.entries(types).map(([key, t]) => {
                const Icon = TYPE_ICONS[key] || Activity;
                const active = logForm.type === key;
                return (
                  <button
                    key={key}
                    onClick={() => setLogForm((f) => ({ ...f, type: key }))}
                    className={`p-3 flex flex-col items-center gap-1 transition-all ${
                      active ? "bg-[#FF4500]/20 border border-[#FF4500]" : "bg-[#050505] hover:bg-white/[0.03]"
                    }`}
                    data-testid={`recovery-type-${key}`}
                  >
                    <Icon className="w-4 h-4" style={{ color: active ? "#FF4500" : t.color }} />
                    <span className="text-[8px] font-mono text-white/60 uppercase">{t.name.split(" ")[0]}</span>
                  </button>
                );
              })}
            </div>

            {/* Sleep fields */}
            {logForm.type === "sleep" ? (
              <>
                <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-2">
                  Hours Slept: {logForm.sleep_hours}h
                </p>
                <input
                  type="range"
                  min="3"
                  max="12"
                  step="0.5"
                  value={logForm.sleep_hours}
                  onChange={(e) => setLogForm({ ...logForm, sleep_hours: parseFloat(e.target.value) })}
                  className="w-full mb-4 accent-[#FF4500]"
                  data-testid="slider-sleep_hours"
                />
                <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-2">
                  Quality: {logForm.sleep_quality}/5
                </p>
                <div className="flex gap-1 mb-4" data-testid="slider-sleep_quality">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setLogForm({ ...logForm, sleep_quality: n })}
                      className={`flex-1 py-2 border text-sm font-heading ${
                        logForm.sleep_quality >= n
                          ? "bg-[#FF4500] border-[#FF4500] text-white"
                          : "bg-transparent border-white/10 text-white/40"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-2">
                  Duration: {logForm.duration_minutes}m
                </p>
                <input
                  type="range"
                  min="5"
                  max="90"
                  step="5"
                  value={logForm.duration_minutes}
                  onChange={(e) => setLogForm({ ...logForm, duration_minutes: parseInt(e.target.value) })}
                  className="w-full mb-4 accent-[#FF4500]"
                  data-testid="slider-duration"
                />
              </>
            )}

            {/* Soreness */}
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mb-2">
              Soreness: {logForm.soreness_level}/10
            </p>
            <input
              type="range"
              min="0"
              max="10"
              value={logForm.soreness_level}
              onChange={(e) => setLogForm({ ...logForm, soreness_level: parseInt(e.target.value) })}
              className="w-full mb-3 accent-[#FF4500]"
              data-testid="slider-soreness"
            />
            <div className="flex flex-wrap gap-1 mb-4">
              {SORENESS_AREAS.map((area) => (
                <button
                  key={area}
                  onClick={() => toggleArea(area)}
                  className={`px-2 py-1 text-[10px] font-mono uppercase tracking-[0.1em] border ${
                    logForm.soreness_areas.includes(area)
                      ? "bg-[#FF4500] border-[#FF4500] text-white"
                      : "border-white/10 text-white/40 hover:border-white/30"
                  }`}
                >
                  {area}
                </button>
              ))}
            </div>

            {/* Notes */}
            <textarea
              placeholder="Notes (optional)..."
              value={logForm.notes}
              onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
              className="w-full bg-black border border-white/10 text-white p-3 mb-4 text-sm focus:border-[#FF4500] outline-none min-h-[60px]"
              data-testid="notes-input"
            />

            <div className="flex gap-2">
              <Button
                onClick={submitLog}
                className="flex-1 bg-[#FF4500] hover:bg-[#FF6326] font-heading uppercase tracking-wider text-xs rounded-none"
                data-testid="recovery-save-btn"
              >
                Save Entry
              </Button>
              <Button
                onClick={() => setShowLog(false)}
                variant="ghost"
                className="text-white/40 rounded-none text-xs font-heading uppercase tracking-wider"
                data-testid="recovery-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Active Session Modal */}
      {activeSession && (
        <div
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setActiveSession(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] border border-white/[0.06] p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            data-testid="active-session-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] font-mono text-[#FF4500] uppercase tracking-[0.3em] mb-1">
                  {activeSession.duration_minutes} Minutes
                </p>
                <h3 className="text-3xl font-heading text-white uppercase">{activeSession.name}</h3>
                <p className="text-xs text-white/40 mt-1">{activeSession.description}</p>
              </div>
              <button onClick={() => setActiveSession(null)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-1">
              {activeSession.exercises.map((ex, i) => (
                <div
                  key={i}
                  className="bg-[#050505] border border-white/[0.06] p-4 flex items-center gap-4"
                  data-testid={`session-exercise-${i}`}
                >
                  <div className="w-8 h-8 bg-[#FF4500]/20 flex items-center justify-center shrink-0">
                    <span className="text-[#FF4500] font-heading text-sm">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white font-heading uppercase tracking-tight">{ex.name}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">{ex.description}</p>
                  </div>
                  <p className="text-xs font-mono text-[#CCFF00]">{ex.duration}</p>
                </div>
              ))}
            </div>

            <Button
              onClick={() => {
                setLogForm((f) => ({
                  ...f,
                  type: activeSession.id.includes("yoga")
                    ? "yoga"
                    : activeSession.id.includes("foam")
                    ? "foam_rolling"
                    : activeSession.id.includes("cold")
                    ? "ice_bath"
                    : "stretching",
                  duration_minutes: activeSession.duration_minutes,
                }));
                setActiveSession(null);
                setShowLog(true);
              }}
              className="w-full mt-5 bg-[#FF4500] hover:bg-[#FF6326] font-heading uppercase tracking-wider text-xs rounded-none"
              data-testid="complete-session-btn"
            >
              <Play className="w-4 h-4 mr-2" /> Mark Complete & Log
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Recovery;
